/**
 * ISO 3166-1 alpha-2 country codes - what the company DB's billing details
 * require (0129 `api.editor_set_billing_details`: "country must be an
 * ISO-3166 alpha-2 code"). `toCountryCode` also accepts a country NAME in
 * English or German ("Deutschland", "Germany") so an older client or a pasted
 * value still lands as the code instead of a DB error.
 *
 * @module web/countryCodes (mirror of the server's country-codes.ts)
 */

export const COUNTRY_CODES: readonly string[] = "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW".split(" ");

const CODE_SET = new Set(COUNTRY_CODES);

function fold(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z]/g, "");
}

let byName: Map<string, string> | null = null;
function nameIndex(): Map<string, string> {
  if (byName) return byName;
  byName = new Map();
  for (const lang of ["en", "de"]) {
    const dn = new Intl.DisplayNames([lang], { type: "region" });
    for (const code of COUNTRY_CODES) {
      const name = dn.of(code);
      if (name) byName.set(fold(name), code);
    }
  }
  return byName;
}

/** "DE", "de", "Deutschland", "Germany" -> "DE"; anything unrecognised is returned trimmed as given (the DB then rejects it with its own message). */
export function toCountryCode(input: string): string {
  const v = input.trim();
  if (CODE_SET.has(v.toUpperCase())) return v.toUpperCase();
  return nameIndex().get(fold(v)) ?? v;
}

/** Options for a country picker, named in the viewer's language and sorted by that name. */
export function countryOptions(locale: string): Array<{ value: string; label: string }> {
  const dn = new Intl.DisplayNames([locale], { type: "region" });
  return COUNTRY_CODES.map((c) => ({ value: c, label: dn.of(c) ?? c })).sort((a, b) => a.label.localeCompare(b.label, locale));
}
